import { useMemo } from 'react';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useGenericGet } from '@hooks/use-api.hook';
import { apiClient } from '@services/api-client.service';
import { useActiveTenant } from '@states/active-tenant.atom';
import type {
  VisaAnalyticsQueryParams,
  VisaAnalyticsResponse,
} from '../types/visa-application-analytics.types';

const useVisaApplicationAnalyticsBasePath = () => {
  const { activeTenant } = useActiveTenant();

  return useMemo(() => {
    if (!activeTenant?.id) return '';
    return `/v1/admin/tenants/${activeTenant.id}/dashboard/analytics/visa-application`;
  }, [activeTenant?.id]);
};

export const buildVisaApplicationAnalyticsUrl = (
  basePath: string,
  params: VisaAnalyticsQueryParams
): string => {
  const query = new URLSearchParams();

  if (params.startDate) {
    query.set('startDate', params.startDate);
  }

  if (params.endDate) {
    query.set('endDate', params.endDate);
  }

  if (params.visaStatus && params.visaStatus.length > 0) {
    query.set(
      'visaStatus',
      params.visaStatus
        .map((status) => status?.toString())
        .filter((status) => !!status)
        .join(',')
    );
  }

  const queryString = query.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

export const useVisaApplicationAnalytics = (
  params: VisaAnalyticsQueryParams,
  queryOptions?: {
    enabled?: boolean;
    staleTime?: number;
  }
) => {
  const basePath = useVisaApplicationAnalyticsBasePath();

  const url = useMemo(() => {
    if (!basePath) return '';
    return buildVisaApplicationAnalyticsUrl(basePath, params);
  }, [basePath, params.startDate, params.endDate, params.visaStatus]);

  const statusKey = useMemo(
    () => (params.visaStatus && params.visaStatus.length ? params.visaStatus.join(',') : ''),
    [params.visaStatus]
  );

  const queryKey: string[] = [
    'VisaApplicationAnalytics',
    basePath,
    params.startDate ?? '',
    params.endDate ?? '',
    statusKey,
  ];

  const options: UseQueryOptions<VisaAnalyticsResponse> = {
    queryKey,
    enabled: !!url && (queryOptions?.enabled ?? true),
    staleTime: queryOptions?.staleTime ?? 5 * 60 * 1000,
  };

  return useGenericGet<VisaAnalyticsResponse>(url, queryKey, apiClient, options);
};

export const useVisaApplicationAnalyticsApi = () => {
  return {
    useGet: useVisaApplicationAnalytics,
  };
};
